import React, { useEffect, useRef, useState } from "react";
import { RotateCcw, Upload, PenSquare, FolderPlus, Check, X, AlertTriangle, Loader2, Trash2, Sparkles, Lock, UserPlus } from "lucide-react";
import { api } from "../../../lib/api";
import TeamInvitesPanel from "./TeamInvitesPanel";

/**
 * Enterprise panel — tabbed workspace for CIO/CFO admin actions.
 *
 *   [Reset]   Zero out current or all projects (two-step confirm)
 *   [Initiatives] Import prebuilt C-Suite programmes (Supply Chain / Finance / …)
 *   [Override]    Manual dashboard rollup override (overlay on Portfolio tab)
 *   [Templates]   $50 unlock + upload up to 3 company templates
 *
 * Used inside CSuiteDashboard as its own "Admin" tab. Basic-portal reset uses
 * <ResetInline/> from this file to reuse the confirm UX.
 */

export function EnterprisePanel({ onDataChanged }) {
  const [tab, setTab] = useState("initiatives");
  const tabs = [
    { key: "initiatives", label: "Initiatives",      icon: FolderPlus },
    { key: "team",        label: "Team · Invites",   icon: UserPlus },
    { key: "override",    label: "Manual Override",  icon: PenSquare },
    { key: "templates",   label: "Templates ($50)",  icon: Upload },
    { key: "reset",       label: "Reset · Danger",   icon: RotateCcw },
  ];
  return (
    <div className="card p-6" data-testid="enterprise-panel">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={14} className="text-cyan-600"/>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Enterprise controls</div>
      </div>
      <div className="font-display text-xl font-extrabold tracking-tight text-zinc-900 mb-4">Admin · Programme controls</div>

      <div className="flex items-center gap-1 border-b border-zinc-200/70 mb-5 -mx-1 px-1 overflow-x-auto">
        {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 border-b-2 ${active ? "border-cyan-600 text-cyan-700" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
              data-testid={`ent-tab-${t.key}`}>
              <Icon size={12}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "initiatives" && <InitiativesTab onImport={onDataChanged}/>}
      {tab === "team"        && <TeamInvitesPanel/>}
      {tab === "override"    && <OverrideTab   onSaved={onDataChanged}/>}
      {tab === "templates"   && <TemplatesTab/>}
      {tab === "reset"       && <ResetTab      onReset={onDataChanged}/>}
    </div>
  );
}


// ── Initiatives ──────────────────────────────────────────────────────────
function InitiativesTab({ onImport }) {
  const [items, setItems] = useState([]);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { api.listInitiatives().then(r => setItems(r.templates || [])).catch(() => {}); }, []);

  const toggle = (k) => setPick(p => ({ ...p, [k]: !p[k] }));
  const submit = async () => {
    const keys = Object.entries(pick).filter(([, v]) => v).map(([k]) => k);
    if (!keys.length) return;
    setBusy(true); setMsg("");
    try {
      const r = await api.importInitiatives(keys);
      setMsg(`${r.created.length} initiative${r.created.length === 1 ? "" : "s"} imported.`);
      setPick({});
      const list = await api.listInitiatives();
      setItems(list.templates || []);
      onImport && onImport();
    } catch (e) { setMsg(e.message || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="ent-initiatives">
      <p className="text-sm text-zinc-700 leading-relaxed mb-4">
        One-click import of C-Suite initiative programmes. Each becomes a real project with a starter RAID list and phase, so the executive rollup reflects them immediately.
      </p>
      <div className="grid md:grid-cols-2 gap-2 mb-4">
        {items.map(t => (
          <label key={t.key} className={`border rounded-sm p-3 flex items-start gap-2 cursor-pointer ${t.already_imported ? "opacity-60 bg-zinc-50 cursor-not-allowed" : pick[t.key] ? "border-cyan-500 bg-cyan-50" : "border-[var(--border)] hover:border-zinc-400"}`} data-testid={`ent-init-${t.key}`}>
            <input type="checkbox" disabled={t.already_imported} checked={!!pick[t.key]} onChange={() => toggle(t.key)} className="mt-1"/>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm font-bold text-zinc-900">{t.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{t.code} · {t.phase}</div>
              {t.already_imported && <div className="font-mono text-[9px] uppercase tracking-widest text-emerald-700 mt-1">already imported ✓</div>}
            </div>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={submit} disabled={busy || Object.values(pick).every(v => !v)} className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="ent-init-import">
          {busy ? <><Loader2 size={12} className="animate-spin"/> Importing…</> : <><FolderPlus size={13}/> Import selected</>}
        </button>
        {msg && <span className="text-xs text-emerald-700" data-testid="ent-init-msg">{msg}</span>}
      </div>
    </div>
  );
}


// ── Override ─────────────────────────────────────────────────────────────
function OverrideTab({ onSaved }) {
  const [o, setO] = useState({ avg_progress: "", on_track_pct: "", budget_variance_pct: "", critical_risks: "", note: "" });
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.getOverride().then(r => {
    const x = r.override || {};
    setO({
      avg_progress: x.avg_progress ?? "",
      on_track_pct: x.on_track_pct ?? "",
      budget_variance_pct: x.budget_variance_pct ?? "",
      critical_risks: x.critical_risks ?? "",
      note: x.note ?? "",
    });
  }).catch(() => {}); }, []);

  const num = (v) => v === "" || v === null ? undefined : Number(v);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.putOverride({
        avg_progress: num(o.avg_progress),
        on_track_pct: num(o.on_track_pct),
        budget_variance_pct: num(o.budget_variance_pct),
        critical_risks: num(o.critical_risks),
        note: o.note || undefined,
      });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      onSaved && onSaved();
    } finally { setBusy(false); }
  };
  const clear = async () => {
    await api.clearOverride();
    setO({ avg_progress: "", on_track_pct: "", budget_variance_pct: "", critical_risks: "", note: "" });
    onSaved && onSaved();
  };

  return (
    <form onSubmit={submit} data-testid="ent-override">
      <p className="text-sm text-zinc-700 leading-relaxed mb-4">
        Overlay manual numbers on top of the executive rollup — useful for board packs where you want to smooth in-flight data. Any field left blank uses the auto-calculated value.
      </p>
      <div className="grid md:grid-cols-4 gap-3 mb-3">
        <NumField label="Avg progress %" v={o.avg_progress} onChange={v => setO({...o, avg_progress: v})} testid="ov-progress"/>
        <NumField label="On-track %"     v={o.on_track_pct} onChange={v => setO({...o, on_track_pct: v})} testid="ov-ontrack"/>
        <NumField label="Budget variance %" v={o.budget_variance_pct} onChange={v => setO({...o, budget_variance_pct: v})} testid="ov-budget"/>
        <NumField label="Critical risks (#)" v={o.critical_risks} onChange={v => setO({...o, critical_risks: v})} testid="ov-risks"/>
      </div>
      <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Board note (optional)</span>
        <textarea value={o.note} onChange={e => setO({...o, note: e.target.value})} rows={2} maxLength={400}
                  className="mt-1 w-full border border-[var(--border)] rounded-sm py-2 px-3 text-sm outline-none focus:border-cyan-500" data-testid="ov-note"/>
      </label>
      <div className="mt-3 flex items-center gap-2">
        <button type="submit" disabled={busy} className="btn btn-primary btn-sm" data-testid="ov-save">{busy ? "Saving…" : "Save override"}</button>
        <button type="button" onClick={clear} className="btn btn-secondary btn-sm" data-testid="ov-clear">Clear override</button>
        {saved && <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="ov-saved"><Check size={12}/> Saved</span>}
      </div>
    </form>
  );
}
function NumField({ label, v, onChange, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input type="number" value={v} onChange={e => onChange(e.target.value)} step="0.1"
             className="mt-1 w-full border border-[var(--border)] rounded-sm py-2 px-3 text-sm outline-none focus:border-cyan-500" data-testid={testid}/>
    </label>
  );
}


// ── Templates ($50 unlock) ────────────────────────────────────────────────
function TemplatesTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);
  const [kind, setKind] = useState("pptx");

  const refresh = () => api.myTemplates().then(setData).catch(() => setData({ unlocked: false, cap: 3, templates: [] }));
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    // Handle return from Stripe checkout
    const u = new URL(window.location.href);
    const sid = u.searchParams.get("session_id");
    if (sid) {
      api.templateStatus(sid).then(() => { refresh(); u.searchParams.delete("session_id"); window.history.replaceState({}, "", u.toString()); });
    }
  }, []);

  const unlock = async () => {
    setBusy(true);
    try {
      const r = await api.templateCheckout();
      window.location.href = r.checkout_url;
    } catch (e) { setMsg(e.message || "Checkout failed"); setBusy(false); }
  };
  const upload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setBusy(true); setMsg("");
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("pmv5_csuite_token") || localStorage.getItem("pmv5_token");
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", f);
      const res = await fetch(`${base}/api/templates/upload`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "Upload failed");
      setMsg(`Uploaded ${j.filename} · ${j.remaining} slots left`);
      fileRef.current.value = "";
      refresh();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };
  const remove = async (fn) => { await api.deleteTemplate(fn); refresh(); };

  if (!data) return <div className="text-sm text-zinc-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Loading…</div>;

  return (
    <div data-testid="ent-templates">
      {!data.unlocked ? (
        <div className="p-4 rounded-sm" style={{ background: "linear-gradient(135deg,#FEF7ED 0%,#FEF3C7 100%)" }}>
          <div className="flex items-center gap-2 mb-1"><Lock size={14} className="text-amber-700"/>
            <div className="font-mono text-[10px] uppercase tracking-widest text-amber-800">One-time unlock · $50</div>
          </div>
          <div className="font-display text-lg font-extrabold tracking-tight">Use your company&rsquo;s own template on every export</div>
          <p className="text-sm text-zinc-700 mt-2 max-w-2xl">
            After a one-time $50 payment, upload up to <b>3 templates</b> (PPTX + DOCX + PDF stationery) and pick them on any Board Pack or export. No monthly fee — unlimited exports per template.
          </p>
          <button onClick={unlock} disabled={busy} className="btn btn-primary btn-sm mt-3 inline-flex items-center gap-1.5" data-testid="tmpl-unlock">
            {busy ? <><Loader2 size={12} className="animate-spin"/> Redirecting…</> : <><Upload size={13}/> Unlock templates · $50</>}
          </button>
        </div>
      ) : (
        <>
          <div className="p-3 mb-4 rounded-sm bg-emerald-50 border border-emerald-200 flex items-center gap-2 text-sm text-emerald-800" data-testid="tmpl-unlocked-banner">
            <Check size={14}/> Templates unlocked · {3 - (data.templates?.length || 0)} of {data.cap} slots free
          </div>

          {data.templates?.length ? (
            <div className="mb-4 space-y-1.5">
              {data.templates.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-2 border border-[var(--border)] rounded-sm text-sm" data-testid={`tmpl-row-${i}`}>
                  <div className="min-w-0 truncate">
                    <span className="pill pill-blue text-[9px] mr-2">{t.kind.toUpperCase()}</span>
                    <span className="font-mono text-xs">{t.filename}</span>
                    <span className="text-[10px] text-zinc-500 ml-2">{Math.round(t.size / 1024)} KB</span>
                  </div>
                  <button onClick={() => remove(t.filename)} className="text-zinc-400 hover:text-red-700" title="Delete" data-testid={`tmpl-del-${i}`}><Trash2 size={12}/></button>
                </div>
              ))}
            </div>
          ) : null}

          {(data.templates?.length || 0) < 3 && (
            <form onSubmit={upload} className="flex flex-col sm:flex-row items-stretch gap-2">
              <select value={kind} onChange={e => setKind(e.target.value)} className="border border-[var(--border)] rounded-sm py-2 px-3 text-sm bg-white" data-testid="tmpl-kind">
                <option value="pptx">PowerPoint (.pptx)</option>
                <option value="docx">Word (.docx)</option>
                <option value="pdf">PDF stationery (.pdf)</option>
              </select>
              <input ref={fileRef} type="file" accept=".pptx,.docx,.pdf" required
                     className="flex-1 border border-[var(--border)] rounded-sm py-2 px-3 text-sm bg-white" data-testid="tmpl-file"/>
              <button type="submit" disabled={busy} className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="tmpl-upload">
                {busy ? "Uploading…" : <><Upload size={13}/> Upload</>}
              </button>
            </form>
          )}
        </>
      )}
      {msg && <div className="mt-2 text-xs text-zinc-700" data-testid="tmpl-msg">{msg}</div>}
    </div>
  );
}


// ── Reset ─────────────────────────────────────────────────────────────────
export function ResetTab({ onReset, currentProjectId, projectLabel, compact }) {
  const [scope, setScope] = useState(currentProjectId ? "one" : "all");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { setScope(currentProjectId ? "one" : "all"); }, [currentProjectId]);

  const canGo = confirmText.trim().toUpperCase() === "RESET";

  const run = async () => {
    if (!canGo) return;
    setBusy(true); setMsg("");
    try {
      if (scope === "one" && currentProjectId) {
        const r = await api.resetProject(currentProjectId);
        setMsg(`Project reset. ${r.reset_at?.slice(0, 10)}`);
      } else {
        const r = await api.resetAllProjects();
        setMsg(`Reset ${r.modified} project${r.modified === 1 ? "" : "s"}.`);
      }
      setConfirmText("");
      onReset && onReset();
    } catch (e) { setMsg(e.message || "Reset failed"); }
    finally { setBusy(false); }
  };

  const box = (
    <>
      <div className="p-3 rounded-sm bg-red-50 border border-red-200 mb-3 flex items-start gap-2" data-testid="reset-warning">
        <AlertTriangle size={14} className="text-red-700 mt-0.5 shrink-0"/>
        <div className="text-xs text-red-900 leading-relaxed">
          <b>Non-reversible.</b> Reset zeros every KPI, RAID, task, action, budget row, requirement, stakeholder and milestone on the selected scope. Project shells remain. Use with care — there&rsquo;s no undo.
        </div>
      </div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {currentProjectId && (
          <label className="inline-flex items-center gap-1.5 text-sm">
            <input type="radio" name="scope" checked={scope === "one"} onChange={() => setScope("one")} data-testid="reset-scope-one"/>
            Only <b className="font-display">{projectLabel || "current project"}</b>
          </label>
        )}
        <label className="inline-flex items-center gap-1.5 text-sm">
          <input type="radio" name="scope" checked={scope === "all"} onChange={() => setScope("all")} data-testid="reset-scope-all"/>
          All projects I own
        </label>
      </div>
      <div className="mb-3">
        <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Type RESET to confirm</label>
        <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="RESET"
               className="w-64 border border-red-300 rounded-sm py-2 px-3 text-sm outline-none focus:border-red-600 uppercase tracking-widest font-mono" data-testid="reset-confirm-input"/>
      </div>
      <button onClick={run} disabled={!canGo || busy} className="btn btn-sm bg-red-700 hover:bg-red-800 disabled:bg-zinc-300 text-white inline-flex items-center gap-1.5" data-testid="reset-execute">
        {busy ? <><Loader2 size={12} className="animate-spin"/> Resetting…</> : <><RotateCcw size={13}/> Reset now</>}
      </button>
      {msg && <div className="mt-2 text-xs text-emerald-700" data-testid="reset-msg">{msg}</div>}
    </>
  );

  if (compact) return <div className="p-3">{box}</div>;
  return (
    <div data-testid="ent-reset">
      <p className="text-sm text-zinc-700 leading-relaxed mb-4">Zero out programme data — useful for restarting a portfolio year or clearing test data before a real go-live.</p>
      {box}
    </div>
  );
}
